/**
 * A moment in the tenant's time zone, in words (wave COM item 1: a task's due time).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The server counts a task's clock, an RFI's agreed day and the witness grace in the
 * tenant's declared zone (tenant.time_zone). No route sends that zone to the client yet
 * (WAVE_NOTES.md, wave b12 frontend, "Backend follow-ups"), so until one does the zone is
 * the browser's, and every moment is printed WITH its zone name so the reader never has
 * to guess which clock it is on. `setTenantTimeZone` is the one place a later read of the
 * tenant's zone plugs in; nothing else changes.
 */

let tenantZone: string | null = null;

/** Record the tenant's declared zone (an IANA name); an unknown name is ignored. */
export function setTenantTimeZone(zone: string | null): void {
  if (zone === null) {
    tenantZone = null;
    return;
  }
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    tenantZone = zone;
  } catch {
    tenantZone = null;
  }
}

/** The zone moments are shown in: the tenant's once known, else the browser's. */
export function tenantTimeZone(): string {
  return tenantZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** "25 Oct 2026, 14:00 GMT+3": the day, the time and the zone, in the tenant's zone. */
export function zonedStamp(iso: string | null, zone: string = tenantTimeZone()): string {
  if (iso === null || iso.length === 0) {
    return "";
  }
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  }).format(moment);
}
